export interface PaymentApp {
    id: string;
    name: string;
    icon: string;
    placeholder: string;
    supportsAmount: boolean;
}

export const PAYMENT_APPS: PaymentApp[] = [
    {
        id: 'venmo',
        name: 'Venmo',
        icon: '💜',
        placeholder: 'Your Venmo username',
        supportsAmount: true,
    },
    {
        id: 'paypal',
        name: 'PayPal',
        icon: '🅿️',
        placeholder: 'Your PayPal.me username',
        supportsAmount: true,
    },
    {
        id: 'revolut',
        name: 'Revolut',
        icon: '🔵',
        placeholder: 'Your Revolut.me username',
        supportsAmount: false,
    },
    {
        id: 'monzo',
        name: 'Monzo',
        icon: '🟠',
        placeholder: 'Your Monzo.me username',
        supportsAmount: true,
    },
    {
        id: 'custom',
        name: 'Custom Link',
        icon: '🔗',
        placeholder: 'https://...',
        supportsAmount: false,
    },
];

/**
 * Build a payment deep-link URL for the given app.
 *
 * @param appId   – one of the PAYMENT_APPS ids
 * @param username – the payee's handle / username (or full URL for 'custom')
 * @param amount  – optional amount to pre-fill
 * @param currency – ISO 4217 currency code (e.g. 'USD', 'GBP')
 * @param note    – optional note / memo
 */
export function buildPaymentUrl(
    appId: string,
    username: string,
    amount?: number,
    currency?: string,
    note?: string,
): string {
    const user = username.trim().replace(/^@/, '');
    const amt = amount !== undefined ? amount.toFixed(2) : undefined;
    const encodedNote = note ? encodeURIComponent(note) : undefined;

    switch (appId) {
        case 'venmo': {
            // Web-based Venmo deep link – works on mobile & redirects to app
            const params = new URLSearchParams();
            params.set('txn', 'pay');
            params.set('recipients', `,${user}`);
            if (amt) params.set('amount', amt);
            if (encodedNote && note) params.set('note', note);
            params.set('audience', 'private');
            return `https://account.venmo.com/pay?${params.toString()}`;
        }

        case 'paypal': {
            // https://paypal.me/username/25USD
            let url = `https://paypal.me/${user}`;
            if (amt && currency) {
                url += `/${amt}${currency.toUpperCase()}`;
            } else if (amt) {
                url += `/${amt}`;
            }
            return url;
        }

        case 'revolut': {
            // Revolut.me links do not support pre-filling amounts via URL parameters
            return `https://revolut.me/${user}`;
        }

        case 'monzo': {
            // https://monzo.me/username/20.99?d=Dinner
            let url = `https://monzo.me/${user}`;
            if (amt) {
                url += `/${amt}`;
            }
            if (encodedNote) {
                url += `?d=${encodedNote}`;
            }
            return url;
        }

        case 'custom':
        default:
            // For custom, the username IS the full URL
            return username.trim();
    }
}

/**
 * Get a PaymentApp definition by its id.
 */
export function getPaymentApp(appId: string): PaymentApp | undefined {
    return PAYMENT_APPS.find((app) => app.id === appId);
}
